import React, { useCallback, useRef, useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Badge,
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Toast,
  ToastBody,
  ToastTitle,
  useId,
  useToastController,
  Toaster,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  Spinner,
} from '@fluentui/react-components';
import { ArrowSync20Regular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import { apiFetch } from '../lib/apiFetch';
import PaymentMethodIcon from './PaymentMethodIcon';

// Hover card surfacing the full details of a single payment row in the
// Order page's Payments accordion.
//
// Data note: the OrderPayment shape currently served by
//   GET /booqable/orders/<id>/payments
// (backend handler: `booqable_order_create_payment` in backend/app.py)
// only exposes id, amount_in_cents, payment_method, mode, description,
// status, paid_at. Several fields shown in this card aren't yet on that
// payload and render as em dashes:
//   - Security deposit (no per-payment deposit split server-side)
//   - Refunded / Refundable amounts (no refund ledger surfaced)
//   - Gateway reference (txn id) — best-effort parsed from `description`
//     when the manual flow encodes "txn=XXX"; otherwise dashed.
// To populate these properly, extend the flatten loop inside
// `booqable_order_create_payment` (backend/app.py) to forward the
// relevant Booqable payment attributes (e.g. amount_refunded_in_cents,
// gateway_reference, deposit_amount_in_cents) on each item.

export interface PaymentRowHoverCardPayment {
  id: string;
  amount_in_cents: number | null;
  payment_method: string | null;
  mode: string | null;
  description: string | null;
  status: string | null;
  paid_at: string | null;
  // false when this is a TWM-side captured txn missing from Booqable —
  // see OrderRightSidebar's OrderPayment interface for the resilience flow.
  booqable_synced?: boolean;
  processor_transaction_id?: string | null;
  // TWM PaymentTransaction UUID the refund endpoint expects. Present on
  // settled card rows (incl. Booqable-sourced rows matched by AN txn id);
  // falls back to `id` when absent.
  transaction_id?: string | null;
  // Cents already refunded against this charge (sum of its refund rows).
  amount_refunded_in_cents?: number | null;
  // Technology fee (cents) charged on top of the base for card payments.
  // amount_in_cents is the order-facing base (incl. tax); the customer's card
  // was charged amount_in_cents + fee_in_cents. 0/absent for ACH, manual,
  // holds, and refunds.
  fee_in_cents?: number | null;
  // True when this is a captured card charge still unsettled at Auth.Net, so
  // a free pre-settlement Void is still possible. Backend checks live against
  // AN's transactionStatus; false/absent once settled (use Refund instead).
  voidable?: boolean;
}

interface PaymentRowHoverCardProps {
  payment: PaymentRowHoverCardPayment;
  /** Resolved staff-facing method label (e.g. "Bank transfer", "Card"). */
  methodLabel: string;
  /** Order ID (Booqable or TWM) — used to invalidate the order-payments
   * query after Capture/Void on authorized rows. */
  orderId?: string;
  /** The original payment row to wrap as the hover trigger. */
  children: React.ReactElement;
}

const useStyles = makeStyles({
  // Wrapping div around the trigger row so the popover anchors to a
  // hover-aware container without disturbing the row's existing layout.
  triggerWrap: {
    display: 'block',
    width: '100%',
  },
  surface: {
    width: '300px',
    ...shorthands.padding('0'),
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    ...shorthands.padding('12px', '14px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  headerAmount: {
    fontSize: '18px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
    lineHeight: '22px',
  },
  headerSubtitle: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    ...shorthands.padding('12px', '14px'),
  },
  referenceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  referenceIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
  },
  reference: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  divider: {
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  rowsBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '13px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
  },
  detailLabel: {
    color: tokens.colorNeutralForeground2,
  },
  detailValue: {
    color: tokens.colorNeutralForeground1,
    textAlign: 'right',
  },
  detailValueBold: {
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
    textAlign: 'right',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('10px', '14px', '12px', '14px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  // Primary-styled Delete button uses the danger palette tokens directly
  // since Fluent v9 doesn't expose a built-in "danger" appearance on Button.
  dangerButton: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteRedBorder2),
    ':hover': {
      backgroundColor: tokens.colorPaletteRedForeground1,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    ':hover:active': {
      backgroundColor: tokens.colorPaletteRedForeground1,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
  // Status pill styling — the pending/warning state uses the white-fill,
  // bright-orange (Pumpkin) border + text warning language (see pillStyle).
  statusPill: {
    flexShrink: 0,
  },
  captureFieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
  },
  captureFieldHelp: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  // Warning banner inside the card body for un-synced captures — white fill
  // with a bright-orange (Pumpkin) border. Mirrors the row-level chip so the
  // warning is visible whether the operator glances at the list or opens the
  // hover card.
  unsyncedBanner: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('6px', '8px'),
    fontSize: '12px',
    lineHeight: '16px',
  },
});

const formatMoney = (cents: number | null | undefined): string => {
  const value = ((cents ?? 0) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$${value}`;
};

// Date format: DD-MM-YYYY hh:mm AM/PM, matching the row's existing
// DD-MM-YYYY format with time appended. Uses the browser's local zone
// (Booqable returns ISO; staff read in their tz, not UTC).
const formatPaymentDateTime = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const hh = String(hours).padStart(2, '0');
  return `${dd}-${mm}-${yyyy} ${hh}:${minutes} ${ampm}`;
};

type StatusKind = 'success' | 'failed' | 'pending';

const resolveStatus = (raw: string | null | undefined): { kind: StatusKind; label: string; verb: string } => {
  const s = (raw || '').toLowerCase();
  if (s === 'paid' || s === 'completed' || s === 'succeeded' || s === 'success') {
    return { kind: 'success', label: 'Success', verb: 'Paid' };
  }
  if (s === 'failed' || s === 'declined' || s === 'error') {
    return { kind: 'failed', label: 'Failed', verb: 'Failed' };
  }
  if (s === 'voided') {
    // Reversed pre-settlement — distinct from a declined/failed charge.
    return { kind: 'failed', label: 'Voided', verb: 'Voided' };
  }
  if (s === 'refunded') {
    return { kind: 'success', label: 'Refunded', verb: 'Refunded' };
  }
  if (s === 'authorized') {
    // Held auth — warning styling (kind=pending) but explicit label/verb so
    // operators don't mistake it for an in-flight charge.
    return { kind: 'pending', label: 'Authorized', verb: 'Authorized' };
  }
  return { kind: 'pending', label: 'Pending', verb: 'Pending' };
};

// Best-effort parse for an Auth.Net transaction id encoded in the
// description (e.g. "txn=120083103732" alongside the manual flow's
// "Method: ach — Date: ..." encoding). Returns null when absent.
const parseGatewayTxn = (description: string | null | undefined): string | null => {
  if (!description) return null;
  const match = description.match(/txn\s*=\s*([A-Za-z0-9_-]+)/i);
  return match ? match[1] : null;
};

type MethodSlug = 'card' | 'ach' | 'cash' | 'check' | 'unknown';

const resolveGatewayLine = (
  payment: PaymentRowHoverCardPayment,
  methodSlug: MethodSlug,
): string => {
  const txn = parseGatewayTxn(payment.description);
  const rawMethod = (payment.payment_method || '').toLowerCase();
  // Card and ACH manual flows go through Auth.Net; manual cash/check do not.
  // The parent-resolved methodSlug is authoritative because Booqable leaves
  // payment_method null for Auth.Net-flow payments (method is encoded in
  // description as `Method: <name>`); fall back to the raw field only when
  // the slug is unknown.
  const isAuthnet = methodSlug === 'card' || methodSlug === 'ach' || !!txn;
  if (!isAuthnet) return '—';
  const subMethod =
    methodSlug === 'card' || methodSlug === 'ach'
      ? methodSlug
      : rawMethod === 'credit_card'
        ? 'card'
        : rawMethod || '—';
  return txn
    ? `Authorize.net (${subMethod}) txn=${txn}`
    : `Authorize.net (${subMethod})`;
};

const resolveSubtitle = (payment: PaymentRowHoverCardPayment, methodLabel: string): string => {
  const mode = (payment.mode || '').toLowerCase();
  const method = (payment.payment_method || '').toLowerCase();
  // Card flows (Auth.Net) — subtitle is just "Card payment". Manual flows
  // surface the sub-method so cash vs check vs bank-transfer are clear.
  if (method === 'card' || method === 'credit_card') return 'Card payment';
  if (mode === 'manual') return `Manual payment: ${methodLabel}`;
  return `${methodLabel} payment`;
};

const PaymentRowHoverCard: React.FC<PaymentRowHoverCardProps> = ({
  payment,
  methodLabel,
  orderId,
  children,
}) => {
  const styles = useStyles();
  const toasterId = useId('payment-hover-toaster');
  const { dispatchToast } = useToastController(toasterId);
  const queryClient = useQueryClient();

  const isAuthorized = (payment.status || '').toLowerCase() === 'authorized';
  // 'approved' = captured but pre-settlement. AN allows voiding any unsettled
  // transaction (auth-only OR captured), so Void is the right primary reversal
  // action — no fees, instant. If AN rejects with "already settled", the user
  // falls back to Refund (post-settlement linked credit; see
  // POST /admin/transactions/<id>/refund — shipped in #314).
  const isApproved = (payment.status || '').toLowerCase() === 'approved';
  // Captured-at-AN-but-missing-from-Booqable resilience case (see backend
  // /booqable/orders/<id>/payments merge logic). Drives the Sync to
  // Booqable affordance in the footer + the warning banner.
  const isUnsynced = payment.booqable_synced === false;

  // Capture/void dialog state. These are surfaced from the popover footer
  // when the row is in 'authorized' state — i.e., it's a TWM
  // payment_transactions row exposed by /booqable/orders/<id>/payments'
  // authorize-merge (not a real Booqable payment yet).
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureAmount, setCaptureAmount] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refunding, setRefunding] = useState(false);

  // Controlled open state with a small dismiss delay so the user can
  // mouse from the row into the popover surface (and onto the action
  // buttons) without it closing mid-traversal. PopoverSurface's own
  // mouseEnter cancels the pending dismiss.
  const [open, setOpen] = useState(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDismiss = useCallback(() => {
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
  }, []);

  const scheduleDismiss = useCallback(() => {
    cancelDismiss();
    dismissTimer.current = setTimeout(() => setOpen(false), 150);
  }, [cancelDismiss]);

  const handleTriggerEnter = useCallback(() => {
    cancelDismiss();
    setOpen(true);
  }, [cancelDismiss]);

  const invalidatePayments = useCallback(() => {
    if (orderId) {
      queryClient.invalidateQueries({ queryKey: ['order-payments', orderId] });
      // Also refresh the header status pill — a refund/capture/void changes
      // the order's paid balance, which Booqable recomputes server-side. The
      // pill reads its own ['order-payment-status'] query (OrderDetails.tsx),
      // so without this it stays stale (e.g. "Paid" after a refund) until
      // staleTime/refocus. The sidebar's own charge flows already invalidate
      // both keys; this keeps the hover-card actions consistent.
      queryClient.invalidateQueries({ queryKey: ['order-payment-status', orderId] });
      // And the Orders list pill, which reads payment_status off
      // queryKeys.orders.all — otherwise it stays stale after a
      // refund/capture/void until staleTime/refocus.
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    }
  }, [orderId, queryClient]);

  const openCaptureDialog = useCallback(() => {
    const cents = payment.amount_in_cents ?? 0;
    setCaptureAmount((cents / 100).toFixed(2));
    setCaptureOpen(true);
    setOpen(false);
  }, [payment.amount_in_cents]);

  const handleCapture = useCallback(async () => {
    const dollars = parseFloat(captureAmount);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      dispatchToast(
        <Toast><ToastTitle>Enter a valid capture amount</ToastTitle></Toast>,
        { intent: 'error' },
      );
      return;
    }
    const cents = Math.round(dollars * 100);
    setCapturing(true);
    try {
      const res = await apiFetch(`/admin/transactions/${encodeURIComponent(payment.id)}/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_in_cents: cents }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        dispatchToast(
          <Toast>
            <ToastTitle>Capture failed</ToastTitle>
            <ToastBody>{data.error || `HTTP ${res.status}`}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
        return;
      }
      if (data.booqable_synced === false) {
        // Money moved at Auth.Net but the Booqable create_payment failed.
        // The captured row will surface in the Payments list with a
        // "Not synced to Booqable" chip + sync action — point the
        // operator there instead of pretending everything worked.
        dispatchToast(
          <Toast>
            <ToastTitle>{`Captured ${formatMoney(cents)} — Booqable sync failed`}</ToastTitle>
            <ToastBody>Retry from the Payments list (Sync to Booqable).</ToastBody>
          </Toast>,
          { intent: 'warning' },
        );
      } else {
        dispatchToast(
          <Toast><ToastTitle>{`Captured ${formatMoney(cents)}`}</ToastTitle></Toast>,
          { intent: 'success' },
        );
      }
      setCaptureOpen(false);
      invalidatePayments();
    } catch (err) {
      dispatchToast(
        <Toast>
          <ToastTitle>Capture error</ToastTitle>
          <ToastBody>{String(err)}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setCapturing(false);
    }
  }, [captureAmount, payment.id, dispatchToast, invalidatePayments]);

  const openVoidDialog = useCallback(() => {
    setVoidOpen(true);
    setOpen(false);
  }, []);

  const handleVoid = useCallback(async () => {
    setVoiding(true);
    try {
      // Synced Booqable-sourced rows are keyed by the Booqable charge id; the
      // void endpoint wants the TWM PaymentTransaction id (or AN txn id), which
      // rides on transaction_id. Auth-only rows carry the TWM id as `id`.
      const target = payment.transaction_id || payment.id;
      const res = await apiFetch(`/admin/transactions/${encodeURIComponent(target)}/void`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        dispatchToast(
          <Toast>
            <ToastTitle>Void failed</ToastTitle>
            <ToastBody>{data.error || `HTTP ${res.status}`}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
        return;
      }
      dispatchToast(
        <Toast><ToastTitle>{isAuthorized ? 'Hold released' : 'Transaction voided'}</ToastTitle></Toast>,
        { intent: 'success' },
      );
      setVoidOpen(false);
      invalidatePayments();
    } catch (err) {
      dispatchToast(
        <Toast>
          <ToastTitle>Void error</ToastTitle>
          <ToastBody>{String(err)}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setVoiding(false);
    }
  }, [payment.id, payment.transaction_id, dispatchToast, invalidatePayments, isAuthorized]);

  const handleSyncBooqable = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await apiFetch(`/admin/transactions/${encodeURIComponent(payment.id)}/sync-booqable`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        dispatchToast(
          <Toast>
            <ToastTitle>Sync to Booqable failed</ToastTitle>
            <ToastBody>{data.error || `HTTP ${res.status}`}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
        return;
      }
      dispatchToast(
        <Toast><ToastTitle>Synced to Booqable</ToastTitle></Toast>,
        { intent: 'success' },
      );
      setOpen(false);
      invalidatePayments();
    } catch (err) {
      dispatchToast(
        <Toast>
          <ToastTitle>Sync error</ToastTitle>
          <ToastBody>{String(err)}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setSyncing(false);
    }
  }, [payment.id, dispatchToast, invalidatePayments]);

  // Cents already refunded against this charge, and what's left to refund.
  const refundedCents = payment.amount_refunded_in_cents ?? 0;
  const refundableCents = Math.max(0, (payment.amount_in_cents ?? 0) - refundedCents);

  const openRefundDialog = useCallback(() => {
    setRefundAmount((refundableCents / 100).toFixed(2));
    setRefundOpen(true);
    setOpen(false);
  }, [refundableCents]);

  const handleRefund = useCallback(async () => {
    const dollars = parseFloat(refundAmount);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      dispatchToast(
        <Toast><ToastTitle>Enter a valid refund amount</ToastTitle></Toast>,
        { intent: 'error' },
      );
      return;
    }
    const cents = Math.round(dollars * 100);
    if (cents > refundableCents) {
      dispatchToast(
        <Toast>
          <ToastTitle>Amount exceeds refundable balance</ToastTitle>
          <ToastBody>Maximum: {formatMoney(refundableCents)}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
      return;
    }
    // The refund endpoint keys on the TWM PaymentTransaction id; fall back to
    // the row id (which is the TWM id for unsynced/authorized rows anyway).
    const target = payment.transaction_id || payment.id;
    setRefunding(true);
    try {
      const res = await apiFetch(`/admin/transactions/${encodeURIComponent(target)}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_in_cents: cents }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        dispatchToast(
          <Toast>
            <ToastTitle>Refund failed</ToastTitle>
            <ToastBody>{data.error || `HTTP ${res.status}`}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
        return;
      }
      if (data.under_review) {
        // Auth.Net accepted the reversal but its risk system is reviewing it
        // (eCheck code 193). It'll post once approved — not a failure, not a
        // completed refund. Say so plainly so the operator doesn't retry (which
        // would stack a second held credit).
        dispatchToast(
          <Toast>
            <ToastTitle>{`Refund submitted — under review`}</ToastTitle>
            <ToastBody>
              Authorize.net is reviewing this {formatMoney(cents)} eCheck refund. It posts once
              approved, typically 1–3 business days. No further action needed.
            </ToastBody>
          </Toast>,
          { intent: 'info' },
        );
      } else if (data.booqable_synced === false) {
        // Money refunded at Auth.Net but the Booqable reversal record failed.
        // Surface it rather than implying full success.
        dispatchToast(
          <Toast>
            <ToastTitle>{`Refunded ${formatMoney(cents)} — Booqable not updated`}</ToastTitle>
            <ToastBody>The refund went through at Authorize.net; the Booqable balance may be stale.</ToastBody>
          </Toast>,
          { intent: 'warning' },
        );
      } else {
        dispatchToast(
          <Toast><ToastTitle>{`Refunded ${formatMoney(cents)}`}</ToastTitle></Toast>,
          { intent: 'success' },
        );
      }
      setRefundOpen(false);
      invalidatePayments();
    } catch (err) {
      dispatchToast(
        <Toast>
          <ToastTitle>Refund error</ToastTitle>
          <ToastBody>{String(err)}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setRefunding(false);
    }
  }, [refundAmount, refundableCents, payment.transaction_id, payment.id, dispatchToast, invalidatePayments]);

  // The parent (OrderRightSidebar) already resolves the method via
  // `resolvePaymentMethod(p)` — which handles Booqable's null
  // payment_method for Auth.Net-flow payments by parsing `Method: <name>`
  // out of description. Normalize that human label into a slug we can
  // pattern-match on for icon + gateway line.
  const methodSlug = React.useMemo<MethodSlug>(() => {
    const l = (methodLabel || '').toLowerCase().trim();
    if (l === 'card' || l === 'credit card' || l === 'credit_card') return 'card';
    if (l === 'bank transfer' || l === 'bank_transfer' || l === 'ach' || l === 'bank') return 'ach';
    if (l === 'cash') return 'cash';
    if (l === 'check' || l === 'cheque') return 'check';
    return 'unknown';
  }, [methodLabel]);

  const status = resolveStatus(payment.status);
  const subtitle = resolveSubtitle(payment, methodLabel);
  const gatewayLine = resolveGatewayLine(payment, methodSlug);
  const formattedDate = formatPaymentDateTime(payment.paid_at);
  const amount = formatMoney(payment.amount_in_cents);

  // Technology fee (card gross-up) charged on top of the base. amount_in_cents
  // is the order-facing base (incl. tax); the card was actually charged
  // base + fee. Surface it so the Total row reflects what the customer paid,
  // not just the order balance. Only shown when a fee was actually charged
  // (card flows) — ACH/manual/holds/refunds carry none.
  const feeCents = payment.fee_in_cents ?? 0;
  const hasFee = feeCents > 0;
  const feeAmount = formatMoney(feeCents);
  const totalChargedCents = (payment.amount_in_cents ?? 0) + feeCents;
  const totalCharged = formatMoney(totalChargedCents);

  // Refund eligibility. Card and ACH (eCheck) both reverse via AN's linked
  // refundTransaction — a card charge credits back to the card, an ACH charge
  // credits back to the bank account (backend resolves the instrument off the
  // settled original). Two things must BOTH hold: a card/ACH method, AND that
  // this row is an actual Authorize.net gateway charge. A manually-recorded
  // payment (cash / check / ACH / bank transfer keyed in for money received
  // off-platform) shares the ACH method slug but has no gateway txn to reverse —
  // the backend 409s on it — so it must not offer Refund. Gateway charges carry
  // the "Authorize.net (...)" description the charge/capture/pay-link flows
  // write (and a processor txn id where the row exposes one); manual rows carry
  // "Method: <name>". Never on a held auth (capture/void those), a refund row,
  // or an in-flight (pending) ACH debit that hasn't settled.
  const isRefundRow = (payment.status || '').toLowerCase() === 'refunded';
  const isPending = (payment.status || '').toLowerCase() === 'pending';
  const isGatewayCharge =
    /authorize\.net/i.test(payment.description || '') || !!payment.processor_transaction_id;
  const refundableGatewayMethod = methodSlug === 'card' || methodSlug === 'ach';
  const refundableMethod = refundableGatewayMethod && isGatewayCharge;
  const supportsRefund = refundableMethod && !isRefundRow && !isAuthorized && !isPending;
  // A card charge AN still reports as pre-settlement can ONLY be reversed by a
  // (free) void — AN rejects a linked refund until the nightly batch settles
  // the charge. Backend sets `voidable` from AN's live settlement state, so we
  // suppress Refund while it's set: the UI must never offer a reversal AN will
  // bounce (#397). Once the charge settles, `voidable` clears and Refund takes
  // over as the only reversal.
  const isUnsettledCharge = !!payment.voidable;
  const canRefund = supportsRefund && refundableCents > 0 && !isUnsettledCharge;

  // Voided (reversed pre-settlement) rows are terminal — no further actions.
  const isVoided = (payment.status || '').toLowerCase() === 'voided';
  // A synced, still-unsettled card capture can be voided for free. Backend
  // sets `voidable` from AN's live settlement state; gate on card so we never
  // offer Void where a Refund itself wouldn't apply.
  const canVoidSynced =
    isUnsettledCharge && refundableMethod && !isRefundRow && !isAuthorized && !isVoided;

  const pillStyle: React.CSSProperties =
    status.kind === 'success'
      ? {
          backgroundColor: tokens.colorPaletteGreenBackground2,
          color: tokens.colorPaletteGreenForeground1,
        }
      : status.kind === 'failed'
        ? {
            backgroundColor: tokens.colorPaletteRedBackground2,
            color: tokens.colorPaletteRedForeground1,
          }
        : {
            // Warning (pending/authorized) pill: white fill, bright-orange
            // (Pumpkin) border + text — accent-not-fill warning language.
            backgroundColor: tokens.colorNeutralBackground1,
            color: tokens.colorPalettePumpkinBorderActive,
            border: `1px solid ${tokens.colorPalettePumpkinBorderActive}`,
          };

  const refundButton = (
    <Button
      appearance="outline"
      size="small"
      onClick={openRefundDialog}
      disabled={!canRefund}
      title={
        !refundableMethod
          ? refundableGatewayMethod
            ? 'Only Authorize.net charges can be refunded here — manual payments are reversed off-platform'
            : 'Refunds are available for card & ACH payments only'
          : isUnsettledCharge
            ? 'Available once this charge settles'
            : isPending
              ? 'Available once this payment settles'
              : refundableCents <= 0
                ? 'Fully refunded'
                : undefined
      }
    >
      Refund
    </Button>
  );

  // Wrap the existing row in a hover-aware span. The Popover is
  // controlled via mouseEnter/mouseLeave on the wrapper + the surface,
  // not via Fluent's default click trigger.
  return (
    <>
      <Toaster toasterId={toasterId} position="top" />
      <Popover
        open={open}
        onOpenChange={(_, data) => setOpen(data.open)}
        positioning="after"
        withArrow
      >
        <PopoverTrigger disableButtonEnhancement>
          <span
            className={styles.triggerWrap}
            onMouseEnter={handleTriggerEnter}
            onMouseLeave={scheduleDismiss}
          >
            {children}
          </span>
        </PopoverTrigger>
        <PopoverSurface
          className={styles.surface}
          onMouseEnter={cancelDismiss}
          onMouseLeave={scheduleDismiss}
        >
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <span className={styles.headerAmount}>
                {status.verb} {amount}
              </span>
              <span className={styles.headerSubtitle}>{subtitle}</span>
            </div>
            <Badge
              appearance="filled"
              shape="rounded"
              className={styles.statusPill}
              style={pillStyle}
            >
              {status.label}
            </Badge>
          </div>
          <div className={styles.body}>
            {isUnsynced && (
              <div className={styles.unsyncedBanner}>
                Captured at Authorize.net but not recorded in Booqable. Use Sync to Booqable below to retry.
              </div>
            )}
            <div className={styles.referenceRow}>
              <PaymentMethodIcon
                method={methodSlug}
                className={styles.referenceIcon}
              />
              <span className={styles.reference}>{gatewayLine}</span>
            </div>
            <div className={styles.divider} />
            <div className={styles.rowsBlock}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Amount</span>
                <span className={styles.detailValue}>{amount}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Security deposit</span>
                {/* Not on current payload — see top-of-file note. */}
                <span className={styles.detailValue}>—</span>
              </div>
              {hasFee && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Technology fee</span>
                  <span className={styles.detailValue}>{feeAmount}</span>
                </div>
              )}
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Total</span>
                <span className={styles.detailValueBold}>
                  {hasFee ? totalCharged : amount}
                </span>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.rowsBlock}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Refunded</span>
                <span className={styles.detailValue}>
                  {refundableMethod && !isRefundRow ? formatMoney(refundedCents) : '—'}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Refundable</span>
                <span className={styles.detailValue}>
                  {refundableMethod && !isRefundRow ? formatMoney(refundableCents) : '—'}
                </span>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.rowsBlock}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Date</span>
                <span className={styles.detailValue}>{formattedDate || '—'}</span>
              </div>
            </div>
          </div>
          <div className={styles.footer}>
            {isAuthorized ? (
              <>
                <Button appearance="outline" size="small" onClick={openVoidDialog}>
                  Void hold
                </Button>
                <Button appearance="primary" size="small" onClick={openCaptureDialog}>
                  Capture
                </Button>
              </>
            ) : isRefundRow ? (
              <span className={styles.captureFieldHelp}>Refund — no further actions.</span>
            ) : isUnsynced ? (
              <>
                {refundButton}
                <Button
                  appearance="primary"
                  size="small"
                  icon={syncing ? <Spinner size="tiny" /> : <ArrowSync20Regular />}
                  onClick={handleSyncBooqable}
                  disabled={syncing}
                >
                  Sync to Booqable
                </Button>
              </>
            ) : isApproved ? (
              <>
                {refundButton}
                <Button appearance="primary" size="small" onClick={openVoidDialog}>
                  Void
                </Button>
              </>
            ) : isVoided ? (
              <span className={styles.captureFieldHelp}>Voided — no further actions.</span>
            ) : canVoidSynced ? (
              // Pre-settlement card charge: Void is the only reversal AN accepts
              // (a refund is rejected until it settles). Show Void alone — Refund
              // takes over once `voidable` clears post-settlement (#397).
              <Button appearance="primary" size="small" onClick={openVoidDialog}>
                Void
              </Button>
            ) : (
              refundButton
            )}
          </div>
        </PopoverSurface>
      </Popover>
      <Dialog open={captureOpen} onOpenChange={(_, data) => setCaptureOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Capture authorized payment</DialogTitle>
            <DialogContent>
              <div className={styles.captureFieldGroup}>
                <span className={styles.captureFieldHelp}>
                  Amount to capture (USD). Maximum: {formatMoney(payment.amount_in_cents)} (authorized).
                </span>
                <Input
                  type="number"
                  value={captureAmount}
                  onChange={(_, data) => setCaptureAmount(data.value)}
                  size="large"
                  disabled={capturing}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCaptureOpen(false)} disabled={capturing}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleCapture} disabled={capturing}>
                {capturing ? <Spinner size="tiny" /> : 'Capture'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={voidOpen} onOpenChange={(_, data) => setVoidOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{isAuthorized ? 'Void this hold?' : 'Void this transaction?'}</DialogTitle>
            <DialogContent>
              {isAuthorized
                ? `Releases the ${formatMoney(payment.amount_in_cents)} hold on the customer's card. This cannot be undone.`
                : `Voids this ${formatMoney(payment.amount_in_cents)} payment before it settles. This cannot be undone.`}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setVoidOpen(false)} disabled={voiding}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleVoid} disabled={voiding}>
                {voiding ? <Spinner size="tiny" /> : (isAuthorized ? 'Void hold' : 'Void')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={refundOpen} onOpenChange={(_, data) => setRefundOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Refund payment</DialogTitle>
            <DialogContent>
              <div className={styles.captureFieldGroup}>
                <span className={styles.captureFieldHelp}>
                  Amount to refund (USD). Maximum: {formatMoney(refundableCents)} refundable
                  {refundedCents > 0 ? ` (${formatMoney(refundedCents)} already refunded)` : ''}.
                  Refunds a settled charge back to the customer's original payment
                  method (card or bank account) and cannot be undone.
                </span>
                <Input
                  type="number"
                  value={refundAmount}
                  onChange={(_, data) => setRefundAmount(data.value)}
                  size="large"
                  disabled={refunding}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRefundOpen(false)} disabled={refunding}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleRefund} disabled={refunding}>
                {refunding ? <Spinner size="tiny" /> : 'Refund'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default PaymentRowHoverCard;
